/**
 * Utility functions for Turkish Wordle gameplay.
 */
import { TILE_STATE, WORD_LENGTH } from './constants';

/**
 * Convert a string to Turkish uppercase.
 * Handles special case: 'i' → 'İ' and 'ı' → 'I'
 */
export const toTurkishUpper = (str) => {
  return str
    .replace(/i/g, 'İ')
    .replace(/ı/g, 'I')
    .toUpperCase();
};

/**
 * Convert a string to Turkish lowercase.
 * Handles special case: 'I' → 'ı' and 'İ' → 'i'
 */
export const toTurkishLower = (str) => {
  return str
    .replace(/I/g, 'ı')
    .replace(/İ/g, 'i')
    .toLowerCase();
};

/**
 * Check if a character is a valid Turkish letter.
 */
export const isTurkishLetter = (char) => {
  const upper = toTurkishUpper(char);
  return /^[A-ZÇĞİÖŞÜ]$/.test(upper);
};

/**
 * Get Turkish-aware character length.
 */
export const turkishLength = (str) => {
  return [...str].length;
};

/**
 * Evaluate a guess against the target word.
 * Returns an array of TILE_STATE values for each letter.
 * Handles repeated letters correctly per Wordle rules.
 *
 * Algorithm:
 * 1. First pass: mark correct positions (green).
 * 2. Second pass: mark present but wrong position (yellow),
 *    respecting remaining letter counts.
 * 3. Everything else is absent (gray).
 */
export const evaluateGuess = (guess, target) => {
  const guessArr = [...toTurkishUpper(guess)];
  const targetArr = [...toTurkishUpper(target)];
  const result = new Array(WORD_LENGTH).fill(TILE_STATE.ABSENT);

  // Track remaining target letters (not yet matched)
  const remaining = {};
  targetArr.forEach((letter) => {
    remaining[letter] = (remaining[letter] || 0) + 1;
  });

  // First pass: exact matches (correct / green)
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessArr[i] === targetArr[i]) {
      result[i] = TILE_STATE.CORRECT;
      remaining[guessArr[i]]--;
    }
  }

  // Second pass: present but wrong position (yellow)
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] !== TILE_STATE.CORRECT) {
      if (remaining[guessArr[i]] && remaining[guessArr[i]] > 0) {
        result[i] = TILE_STATE.PRESENT;
        remaining[guessArr[i]]--;
      }
    }
  }

  return result;
};

/**
 * Check if a guess is fully correct.
 */
export const isCorrectGuess = (evaluation) => {
  return evaluation.every((state) => state === TILE_STATE.CORRECT);
};

/**
 * Validate that a guess meets basic format requirements.
 * Returns { valid: boolean, error: string|null }
 */
export const validateGuessFormat = (guess) => {
  if (!guess || turkishLength(guess) !== WORD_LENGTH) {
    return { valid: false, error: 'Kelime 5 harf olmalıdır.' };
  }

  const upper = toTurkishUpper(guess);
  for (const ch of upper) {
    if (!isTurkishLetter(ch)) {
      return { valid: false, error: 'Geçersiz karakter.' };
    }
  }

  return { valid: true, error: null };
};

/**
 * Validate a guess against the dictionary.
 */
export const validateGuess = (guess, dictionary) => {
  const formatResult = validateGuessFormat(guess);
  if (!formatResult.valid) return formatResult;

  const upper = toTurkishUpper(guess);
  if (!dictionary.includes(upper)) {
    return { valid: false, error: 'Kelime listesinde bulunamadı.' };
  }

  return { valid: true, error: null };
};

/**
 * Generate emoji share grid from game history.
 */
export const generateShareText = (guesses, evaluations, gameMode, won) => {
  const header = `OS Wordle 🇹🇷 ${won ? evaluations.length : 'X'}/${6}`;
  const grid = evaluations
    .map((eval_row) =>
      eval_row
        .map((state) => {
          switch (state) {
            case TILE_STATE.CORRECT:
              return '🟩';
            case TILE_STATE.PRESENT:
              return '🟨';
            case TILE_STATE.ABSENT:
              return '⬛';
            default:
              return '⬜';
          }
        })
        .join('')
    )
    .join('\n');

  return `${header}\n\n${grid}`;
};
